import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { EnemyPlane, type EnemyType } from './EnemyPlane';
import { Turret } from './Turret';
import { useStore } from '../../state/store';

/**
 * Sky Carrier — a WW1 fantasy boss: a flat wooden flight deck supported by 4
 * zeppelin pods. Launches fresh enemies from the rear hangar every 15 s.
 *
 * Players damage individual pods. Once two or more pods are destroyed, the carrier
 * loses lift and begins an asymmetric tumble: it rotates around the horizontal axis
 * perpendicular to where lift has failed, falls under gravity, and on ground impact
 * triggers the largest explosion in the game.
 */
const POD_HP = 25;
const POD_OFFSETS: [number, number, number][] = [
  // [x_offset, z_offset, label-id-suffix]
  [-18, -28, 0],  // front-left  pod0
  [18, -28, 0],  // front-right pod1
  [-18, 28, 0],  // back-left   pod2
  [18, 28, 0],  // back-right  pod3
];
const POD_Y = 32; // pods float ABOVE the deck and hold it up via cables
const SPAWN_INTERVAL = 15;
const ORBIT_SPEED = 8; // u/s tangential
/** Exported so the Scene keeps this orbit ring clear of cumulus — the carrier must be SEEN. */
export const CARRIER_ORBIT_RADIUS = 1700;
const ORBIT_RADIUS = CARRIER_ORBIT_RADIUS;
const ALTITUDE = 300; // deck altitude — pods sit at ALTITUDE + POD_Y ≈ 332

interface Pod {
  hp: number;
  object: THREE.Object3D;
  cable: THREE.Object3D;
  ignited: boolean;
  smokeTimer: number;
  /** Once detached from the carrier the pod falls under its own physics. */
  falling: boolean;
  fallVel: THREE.Vector3;
  fallAng: THREE.Vector3;
  grounded: boolean;
}

type DyingStage = 'alive' | 'falling' | 'impact' | 'done';

export class SkyCarrier extends Entity {
  pods: Pod[] = [];
  spawnTimer = SPAWN_INTERVAL * 0.6; // first plane appears a bit faster
  orbitAngle = 0;
  // Tumble physics
  vel = new THREE.Vector3();
  angVel = new THREE.Vector3();
  dyingStage: DyingStage = 'alive';
  dieTimer = 0;
  /** Drop the height-clamping that keeps live carriers in the sky. */
  readonly forward = new THREE.Vector3(0, 0, -1);
  speed = ORBIT_SPEED;

  constructor() {
    super('zeppelin');
    this.hp = 999; // pods carry the actual HP
    this.radius = 60;

    // Orbit start
    this.orbitAngle = Math.random() * Math.PI * 2;
    this.group.position.set(
      Math.cos(this.orbitAngle) * ORBIT_RADIUS,
      ALTITUDE,
      Math.sin(this.orbitAngle) * ORBIT_RADIUS,
    );

    const mesh = makeCarrierMesh();
    this.group.add(mesh);

    // Pods are children of the carrier, positioned at fixed offsets.
    const podMeshGroup = new THREE.Group();
    podMeshGroup.position.set(0, POD_Y, 0);
    this.group.add(podMeshGroup);
    for (let i = 0; i < POD_OFFSETS.length; i++) {
      const [ox, oz] = POD_OFFSETS[i];
      const podObj = makePodMesh();
      podObj.position.set(ox, 0, oz);
      podMeshGroup.add(podObj);
      // Cables run UP from the deck to each pod's underside (gondola). POD_Y is positive
      // (pods above the deck), so the cable midpoint is at POD_Y / 2 ≈ 16 — connecting
      // deck-top to pod-bottom in carrier-local space.
      const cableLen = Math.abs(POD_Y) - 4;
      const cable = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.22, cableLen, 10),
        new THREE.MeshStandardMaterial({ color: 0x141210, roughness: 0.95, metalness: 0.3 }),
      );
      cable.position.set(ox, POD_Y / 2, oz);
      this.group.add(cable);
      this.pods.push({
        hp: Math.round(POD_HP * bossHpScale()), object: podObj, cable, ignited: false, smokeTimer: 0,
        falling: false, fallVel: new THREE.Vector3(), fallAng: new THREE.Vector3(),
        grounded: false,
      });
    }

    // Multi-part hit definition — only the 4 pods are damageable. No deck catch-all,
    // otherwise the deck eats shots aimed at the pods and the boss becomes invulnerable.
    this.hitParts = [
      ...this.pods.map((p, i) => ({
        id: `pod${i}`,
        box: new THREE.Box3(new THREE.Vector3(-14, -5, -7), new THREE.Vector3(14, 5, 7)),
        object: p.object,
      })),
    ];

    // Deck flak emplacements (corners) — defensive guns that fire on the player.
    for (const [fx, fz] of [[-14, -20], [14, -20], [-14, 20], [14, 20]] as [number, number][]) {
      const t = new Turret({ range: 950, interval: 0.85, speed: 250, size: 2.6 });
      t.attachTo(this.group, fx, 3, fz);
      this.turrets.push(t);
    }
  }

  private turrets: Turret[] = [];

  alive(): boolean { return this.dyingStage === 'alive'; }

  /** Detached pods live in the scene root, not under `this.group`, so the base
   *  Entity.dispose() doesn't see them. Tear them down explicitly here.
   *  ORDER MATTERS: dispose detached pods BEFORE `super.dispose()` because the base
   *  call detaches `this.group` from the scene. */
  dispose(): void {
    for (const pod of this.pods) {
      if (pod.falling && pod.object.parent && pod.object.parent !== this.group) {
        pod.object.parent.remove(pod.object);
        pod.object.traverse((o) => {
          const r = o as THREE.Mesh | THREE.Line | THREE.Points;
          const isRender = (r as THREE.Mesh).isMesh || (r as THREE.Line).isLine || (r as THREE.Points).isPoints;
          if (isRender) {
            const rm = r as THREE.Mesh;
            rm.geometry?.dispose?.();
            const mat = rm.material as THREE.Material | THREE.Material[] | undefined;
            if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
            else mat?.dispose?.();
          }
        });
      }
    }
    super.dispose();
  }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.dyingStage !== 'alive') return;
    const idx = parseInt(partId.replace('pod', ''), 10);
    const pod = this.pods[idx];
    if (!pod || pod.hp <= 0) return;
    pod.hp -= amount;
    new Explosion(this.podWorldPos(idx), 5, 0.3, 'fire').addTo(ctx);
    if (pod.hp <= 0) this.ignitePod(idx, ctx);
  }

  private podWorldPos(idx: number): THREE.Vector3 {
    const v = new THREE.Vector3();
    this.pods[idx].object.getWorldPosition(v);
    return v;
  }

  private ignitePod(idx: number, ctx: EntityCtx) {
    const pod = this.pods[idx];
    pod.ignited = true;
    const wp = this.podWorldPos(idx);
    new Explosion(wp, 32, 0.7, 'fire').addTo(ctx);
    new Explosion(wp, 22, 4, 'smoke').setDrift(new THREE.Vector3(0, 10, 0)).addTo(ctx);
    ctx.game.audio.playBoom(0.9);
    // Wounded carrier: deck flak crews fire faster from the first pod loss.
    for (const t of this.turrets) t.intervalScale = 0.6;
    // Stop attracting fire / reticles for this pod.
    if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== `pod${idx}`);
    // Visually scorch the pod
    pod.object.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        const mat = m.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
        if (Array.isArray(mat)) mat.forEach((mm) => mm.color.setHex(0x2a2018));
        else mat.color.setHex(0x2a2018);
      }
    });
    // Snap the cable — it's no longer holding anything.
    pod.cable.visible = false;
    // Re-parent the pod to the scene root so it falls independently of the carrier's
    // motion. THREE's `attach()` preserves the world transform across re-parenting.
    ctx.scene.attach(pod.object);
    pod.falling = true;
    // Initial fall velocity: a touch of the carrier's tangential velocity + a downward
    // dip from losing lift, plus random sideways drift.
    pod.fallVel.set(
      (Math.random() - 0.5) * 6,
      -4 - Math.random() * 3,
      (Math.random() - 0.5) * 6,
    );
    // Tumble axis — random horizontal axis with a slow rotation rate.
    const ax = new THREE.Vector3(Math.random() - 0.5, (Math.random() - 0.5) * 0.3, Math.random() - 0.5).normalize();
    pod.fallAng.copy(ax).multiplyScalar(0.6 + Math.random() * 0.4);
    // 2+ pods destroyed → start the carrier death dive
    const dead = this.pods.filter((p) => p.hp <= 0).length;
    if (dead >= 2 && this.dyingStage === 'alive') this.beginDying(ctx);
  }

  /** Per-frame update for any pod that has been blown loose from the carrier. */
  private updateFallingPods(ctx: EntityCtx) {
    const dt = ctx.dt;
    for (const pod of this.pods) {
      if (!pod.falling || pod.grounded) continue;
      // Gravity + integrate
      pod.fallVel.y -= 14 * dt;
      pod.object.position.addScaledVector(pod.fallVel, dt);
      // Apply angular velocity as an axis-angle quaternion delta.
      const omega = pod.fallAng.clone().multiplyScalar(dt);
      const m = omega.length();
      if (m > 1e-6) {
        const axis = omega.divideScalar(m);
        const dq = new THREE.Quaternion().setFromAxisAngle(axis, m);
        pod.object.quaternion.premultiply(dq);
      }
      // Spew flame + smoke as it falls. 0.12s cadence keeps the trail readable without
      // dumping ~60 sprites/sec onto a low-end mobile GPU when 4 pods are falling.
      pod.smokeTimer += dt;
      if (pod.smokeTimer >= 0.12) {
        pod.smokeTimer = 0;
        const here = pod.object.position;
        new Explosion(here.clone(), 16, 3.0, 'smoke')
          .setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
        if (Math.random() < 0.2) {
          new Explosion(here.clone(), 8, 0.5, 'fire').addTo(ctx);
        }
      }
      // Ground impact
      if (pod.object.position.y <= -460) {
        pod.object.position.y = -460;
        pod.fallVel.set(0, 0, 0);
        pod.fallAng.set(0, 0, 0);
        pod.grounded = true;
        new Explosion(pod.object.position.clone(), 90, 1.1, 'fire').addTo(ctx);
        new Explosion(pod.object.position.clone(), 70, 0.7, 'fire').addTo(ctx);
        for (let i = 0; i < 3; i++) {
          new Explosion(
            pod.object.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 14, 4, (Math.random() - 0.5) * 14)),
            36 + i * 8, 5 + i, 'smoke',
          ).setDrift(new THREE.Vector3(0, 12, 0)).addTo(ctx);
        }
        ctx.game.audio.playBoom(0.9);
      }
    }
  }

  private beginDying(ctx: EntityCtx) {
    this.dyingStage = 'falling';
    this.dieTimer = 0;
    useStore.getState().registerKill(5000);
    ctx.game.audio.playHydrogenRoar();
    // Asymmetric tilt — compute the direction "support is missing" by averaging the
    // offsets of the LIVE pods. Tilt the carrier away from that point (toward the dead
    // pods' side). The horizontal axis perpendicular to that direction is the rotation axis.
    const liveCenter = new THREE.Vector3();
    let liveCount = 0;
    for (let i = 0; i < this.pods.length; i++) {
      if (this.pods[i].hp > 0) {
        liveCenter.add(new THREE.Vector3(POD_OFFSETS[i][0], 0, POD_OFFSETS[i][1]));
        liveCount++;
      }
    }
    if (liveCount > 0) liveCenter.multiplyScalar(1 / liveCount);
    // The rotation axis is the horizontal vector perpendicular to liveCenter.
    const axis = new THREE.Vector3(-liveCenter.z, 0, liveCenter.x);
    if (axis.lengthSq() < 1e-4) axis.set(1, 0, 0);
    axis.normalize();
    // Initial angular velocity around that axis — tilts away from live side.
    const rate = 0.45 + (4 - liveCount) * 0.15; // more gone = faster tumble
    this.angVel.copy(axis).multiplyScalar(rate);
    // Initial sag: a bit of downward velocity right away.
    this.vel.set(0, -8, 0);
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    // Falling pods always tick — they're independent of the carrier's stage.
    this.updateFallingPods(ctx);
    if (this.dyingStage === 'alive') {
      // Slow horizontal orbit
      this.orbitAngle += (ORBIT_SPEED / ORBIT_RADIUS) * dt;
      this.group.position.set(
        Math.cos(this.orbitAngle) * ORBIT_RADIUS,
        ALTITUDE,
        Math.sin(this.orbitAngle) * ORBIT_RADIUS,
      );
      // Face along tangent (deck pointed forward)
      const tangentAngle = this.orbitAngle + Math.PI / 2;
      this.group.rotation.set(0, -tangentAngle, 0);
      // Update forward for Doppler
      this.forward.set(Math.cos(tangentAngle), 0, Math.sin(tangentAngle));
      // Spawn a fresh enemy from the hangar — ESCALATES once it's losing pods: a wounded
      // carrier scrambles everything it has, so finishing it is urgent, not a formality.
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = this.pods.some((p) => p.hp <= 0) ? SPAWN_INTERVAL * 0.55 : SPAWN_INTERVAL;
        this.launchPlane(ctx);
      }
      // Deck flak tracks and fires on the player.
      for (const t of this.turrets) t.update(ctx, this.id);
      return;
    }

    if (this.dyingStage === 'falling') {
      this.dieTimer += dt;
      // Gravity ramps up as the lift collapses.
      this.vel.y -= 18 * dt;
      this.group.position.addScaledVector(this.vel, dt);
      // Apply angular velocity (axis-angle rotation each frame).
      const omega = this.angVel.clone().multiplyScalar(dt);
      const angle = omega.length();
      if (angle > 1e-6) {
        const ax = omega.divideScalar(angle);
        const dq = new THREE.Quaternion().setFromAxisAngle(ax, angle);
        this.group.quaternion.premultiply(dq);
      }
      // Tumbling speeds up over time as more lift fails.
      this.angVel.multiplyScalar(1.0 + 0.35 * dt);
      // (Ignited pods are detached and tumbling independently — updateFallingPods()
      // emits their smoke and flame trails.)
      // Random small fireballs along the deck during the fall
      if (Math.random() < 0.35) {
        const p = this.group.position.clone();
        const local = new THREE.Vector3(
          (Math.random() - 0.5) * 24,
          (Math.random() - 0.5) * 4,
          (Math.random() - 0.5) * 78,
        ).applyQuaternion(this.group.quaternion);
        p.add(local);
        new Explosion(p, 14, 0.6, 'fire').addTo(ctx);
      }
      // Ground hit → MASSIVE explosion
      if (this.group.position.y <= -460) {
        this.dyingStage = 'impact';
        this.massiveImpact(ctx);
        // The carrier's hull crushes a wide swathe of forest, buildings and units.
        const gc = this.group.position.clone();
        gc.setY(ctx.game.gameScene.heightAt(gc.x, gc.z));
        ctx.game.crashImpact(ctx, gc, 170, 30, this);
      }
      return;
    }

    if (this.dyingStage === 'impact') {
      this.dieTimer = 0;
      this.dyingStage = 'done';
    }
    if (this.dyingStage === 'done') {
      this.dieTimer += dt;
      // Linger briefly emitting smoke pillars; then dispose.
      if (this.dieTimer < 6) {
        if (Math.random() < 0.4) {
          const p = this.group.position.clone();
          p.x += (Math.random() - 0.5) * 80;
          p.z += (Math.random() - 0.5) * 80;
          p.y = -460;
          new Explosion(p, 50, 6, 'smoke').setDrift(new THREE.Vector3(0, 14, 0)).addTo(ctx);
        }
      } else {
        this.dead = true;
      }
    }
  }

  private launchPlane(ctx: EntityCtx) {
    // Spawn from the hangar door — slightly behind the carrier center, at deck level.
    const launchLocal = new THREE.Vector3(0, -2, -2);
    launchLocal.applyQuaternion(this.group.quaternion);
    const pos = this.group.position.clone().add(launchLocal);
    // Bias type — mostly scouts (carrier delivers fast harassers), occasional standard.
    const types: EnemyType[] = ['scout', 'scout', 'standard'];
    const t = types[Math.floor(Math.random() * types.length)];
    const enemy = new EnemyPlane(pos, t);
    // Initial heading toward player
    enemy.group.lookAt(ctx.game.player.position);
    ctx.game.add(enemy);
    // Visual puff at the launch
    new Explosion(pos.clone(), 6, 0.4, 'smoke')
      .setDrift(new THREE.Vector3(0, 4, 0)).addTo(ctx);
  }

  private massiveImpact(ctx: EntityCtx) {
    const ground = this.group.position.clone();
    ground.y = -460;
    // Multi-stage giant fireball — bigger than the existing zeppelin death.
    new Explosion(ground.clone(), 380, 1.6, 'fire').addTo(ctx);
    new Explosion(ground.clone(), 240, 1.0, 'fire').addTo(ctx);
    new Explosion(ground.clone(), 140, 0.6, 'fire').addTo(ctx);
    // Wide ring of secondary fireballs
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const r = 50 + Math.random() * 30;
      const p = ground.clone();
      p.x += Math.cos(a) * r;
      p.z += Math.sin(a) * r;
      new Explosion(p, 80 + Math.random() * 30, 0.7, 'fire').addTo(ctx);
    }
    // Towering smoke columns
    for (let i = 0; i < 8; i++) {
      const off = new THREE.Vector3((Math.random() - 0.5) * 90, 5, (Math.random() - 0.5) * 90);
      new Explosion(ground.clone().add(off), 100 + Math.random() * 40, 8 + i, 'smoke')
        .setDrift(new THREE.Vector3((Math.random() - 0.5) * 4, 18, (Math.random() - 0.5) * 4))
        .addTo(ctx);
    }
    // Audio: huge boom + roar, double-stacked
    ctx.game.audio.playBoom(2.0);
    ctx.game.audio.playHydrogenRoar();
    // Camera-attached white screen flash, longest in the game
    this.spawnImpactFlash(ctx);
  }

  private spawnImpactFlash(ctx: EntityCtx) {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.92,
      depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    });
    const geom = new THREE.PlaneGeometry(2, 2);
    const quad = new THREE.Mesh(geom, mat);
    quad.renderOrder = 999;
    quad.frustumCulled = false;
    const cam = ctx.game.camera;
    cam.add(quad);
    quad.position.set(0, 0, -0.6);
    const duration = 1.1;
    let t = 0;
    const tick = () => {
      t += 1 / 60;
      const k = Math.max(0, 1 - t / duration);
      mat.opacity = 0.92 * k;
      if (k <= 0) {
        quad.removeFromParent();
        mat.dispose();
        geom.dispose();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

/* --------------------------------------------------------------- */
/* Mesh factories — kept local to this file.                       */
/* --------------------------------------------------------------- */

function makeCarrierMesh(): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x6a4a30, roughness: 0.9 });
  const woodDark = new THREE.MeshStandardMaterial({ color: 0x4a3220, roughness: 0.95 });
  const iron = new THREE.MeshStandardMaterial({ color: 0x35353a, roughness: 0.45, metalness: 0.7 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x202024, roughness: 0.5, metalness: 0.5 });
  const hangarRoof = new THREE.MeshStandardMaterial({ color: 0x3a2c1a, roughness: 0.85 });
  const stripe = new THREE.MeshStandardMaterial({
    color: 0xe9e0c4, roughness: 0.95,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  const trim = new THREE.MeshStandardMaterial({ color: 0x222020, roughness: 0.85 });

  // Deck: 25 wide × 80 long × 1 thick
  const deck = new THREE.Mesh(new THREE.BoxGeometry(26, 1.2, 80, 6, 2, 24), wood);
  deck.position.set(0, 0, 0);
  g.add(deck);
  // Plank lines along the deck
  for (let i = -3; i <= 3; i++) {
    const plank = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 80, 1, 1, 24), woodDark);
    plank.position.set(i * 3.5, 0.62, 0);
    g.add(plank);
  }
  // Center runway stripe
  const runway = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 60), stripe);
  runway.rotation.x = -Math.PI / 2;
  runway.position.set(0, 0.62, -8);
  g.add(runway);
  // Touchdown markers (cross stripes near the front)
  for (let i = 0; i < 4; i++) {
    const cross = new THREE.Mesh(new THREE.PlaneGeometry(8, 0.6), stripe);
    cross.rotation.x = -Math.PI / 2;
    cross.position.set(0, 0.62, -22 - i * 4);
    g.add(cross);
  }
  // Iron deck-edge railing
  for (const sx of [-12.6, 12.6]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.25, 1.1, 80, 1, 2, 24), iron);
    rail.position.set(sx, 1.2, 0);
    g.add(rail);
    for (let z = -38; z <= 38; z += 6) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 1.2, 8), iron);
      post.position.set(sx, 1.0, z);
      g.add(post);
    }
  }

  // Hangar at the rear (z > 0): big rectangular building with sliding doors.
  const hangar = new THREE.Group();
  hangar.position.set(0, 0.6, 22);
  g.add(hangar);
  const wall = new THREE.Mesh(new THREE.BoxGeometry(20, 9, 22, 4, 6, 6), iron);
  wall.position.set(0, 4.5, 0);
  hangar.add(wall);
  // Roof
  const roof = new THREE.Mesh(new THREE.BoxGeometry(21, 0.8, 23, 4, 1, 6), hangarRoof);
  roof.position.set(0, 9.4, 0);
  hangar.add(roof);
  // Front sliding doors (facing -Z)
  const doorL = new THREE.Mesh(new THREE.BoxGeometry(9.5, 8, 0.4, 4, 4, 1), ironDark);
  doorL.position.set(-5, 4.5, -11.05);
  hangar.add(doorL);
  const doorR = doorL.clone();
  doorR.position.set(5, 4.5, -11.05);
  hangar.add(doorR);
  // Dark center seam
  const seam = new THREE.Mesh(new THREE.BoxGeometry(0.2, 8, 0.5, 1, 4, 1), trim);
  seam.position.set(0, 4.5, -11.0);
  hangar.add(seam);
  // Hangar roof signage stripe
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(16, 1.4), stripe);
  sign.position.set(0, 4.5, -11.3);
  hangar.add(sign);

  // Catapult rail down the front
  const rail = new THREE.Mesh(new THREE.BoxGeometry(2, 0.4, 50, 2, 1, 16), ironDark);
  rail.position.set(0, 0.85, -16);
  g.add(rail);
  for (let z = -38; z <= 6; z += 5) {
    const tie = new THREE.Mesh(new THREE.BoxGeometry(3.5, 0.2, 0.6, 2, 1, 1), ironDark);
    tie.position.set(0, 0.74, z);
    g.add(tie);
  }

  // Two AA-gun emplacements port and starboard near the bow
  for (const sx of [-10, 10]) {
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.4, 0.6, 24), iron);
    ring.position.set(sx, 0.95, -34);
    g.add(ring);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 3.0, 16), iron);
    barrel.rotation.x = -1.0;
    barrel.position.set(sx, 1.5, -36);
    g.add(barrel);
  }

  // Big black cross identifier (nationality marker) on the deck center
  const crossSq = new THREE.Mesh(new THREE.PlaneGeometry(8, 8), stripe);
  crossSq.rotation.x = -Math.PI / 2;
  crossSq.position.set(0, 0.65, 6);
  g.add(crossSq);
  const crossH = new THREE.Mesh(new THREE.PlaneGeometry(7, 1.8), trim);
  crossH.rotation.x = -Math.PI / 2;
  crossH.position.set(0, 0.66, 6);
  g.add(crossH);
  const crossV = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 7), trim);
  crossV.rotation.x = -Math.PI / 2;
  crossV.position.set(0, 0.66, 6);
  g.add(crossV);

  // Underside reinforcement spars
  const sparMat = new THREE.MeshStandardMaterial({ color: 0x3a2820, roughness: 0.9 });
  for (let i = -3; i <= 3; i++) {
    const spar = new THREE.Mesh(new THREE.BoxGeometry(28, 0.6, 1.2, 4, 1, 1), sparMat);
    spar.position.set(0, -1.0, i * 12);
    g.add(spar);
  }
  return g;
}

function makePodMesh(): THREE.Group {
  const g = new THREE.Group();
  const fabric = new THREE.MeshStandardMaterial({ color: 0xafa98c, roughness: 0.95 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x615a4a, roughness: 0.9 });
  const gondola = new THREE.MeshStandardMaterial({ color: 0x5a4632, roughness: 0.9 });
  // Envelope (~ 28 long × 8 wide × 8 tall)
  const env = new THREE.Mesh(new THREE.SphereGeometry(4, 56, 32), fabric);
  env.scale.set(3.5, 1, 1.2);
  g.add(env);
  // Tail fins
  const fin1 = new THREE.Mesh(new THREE.BoxGeometry(2.5, 2.5, 0.2, 4, 4, 2), trim);
  fin1.position.set(-13, 1.2, 0);
  g.add(fin1);
  const fin2 = new THREE.Mesh(new THREE.BoxGeometry(2.5, 0.2, 2.5, 4, 2, 4), trim);
  fin2.position.set(-13, 0, 0);
  g.add(fin2);
  // Small gondola
  const gond = new THREE.Mesh(new THREE.BoxGeometry(3.5, 1.3, 1.8, 4, 2, 3), gondola);
  gond.position.set(0, -3.8, 0);
  g.add(gond);
  return g;
}
